import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, Sun, Moon, AlertTriangle, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ParsedDayHours {
  open: boolean;
  start?: string;
  end?: string;
}

interface OperatingHours {
  [key: string]: string | ParsedDayHours;
}

interface ServiceOperatingHoursDisplayProps {
  operatingHours?: OperatingHours;
  aogSupport?: boolean;
  aogResponseTime?: string;
  shiftCoverage?: string;
  timezone?: string;
}

const DAYS_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS: Record<string, string> = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun'
};

const ServiceOperatingHoursDisplay: React.FC<ServiceOperatingHoursDisplayProps> = ({
  operatingHours,
  aogSupport,
  aogResponseTime,
  shiftCoverage,
  timezone
}) => {
  // Parse operating hours - handles both string format "09:00-17:00" and object format {open, start, end}
  const parseDayHours = (dayData: string | ParsedDayHours | undefined): ParsedDayHours => {
    if (!dayData) return { open: false };
    
    // Already in object format
    if (typeof dayData === 'object') {
      return dayData;
    }
    
    // String format - check for "closed"
    if (dayData === 'closed' || dayData === '') {
      return { open: false };
    }
    
    // Parse "09:00-17:00" or "09:00 - 17:00" format
    const timeParts = dayData.split(/\s*-\s*/);
    if (timeParts.length === 2) {
      return { open: true, start: timeParts[0].trim(), end: timeParts[1].trim() };
    }
    
    return { open: false };
  };

  // Check if we have any hours data
  const hasHoursData = operatingHours && Object.keys(operatingHours).length > 0;
  
  if (!hasHoursData && !aogSupport && !shiftCoverage) return null;

  // Format time (24h to 12h)
  const formatTime = (time: string) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  // Get current day
  const currentDay = DAYS_ORDER[new Date().getDay() === 0 ? 6 : new Date().getDay() - 1];

  // Calculate if currently open
  const isCurrentlyOpen = () => {
    if (!operatingHours) return null;
    const todayRaw = operatingHours[currentDay];
    const today = parseDayHours(todayRaw);
    if (!today.open || !today.start || !today.end) return false;
    
    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    return currentTime >= today.start && currentTime <= today.end;
  };

  const currentlyOpen = isCurrentlyOpen();

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" />
            Operating Hours
          </CardTitle>
          {currentlyOpen !== null && (
            <Badge className={cn(
              "text-xs",
              currentlyOpen 
                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" 
                : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
            )}>
              {currentlyOpen ? (
                <>
                  <Check className="w-3 h-3 mr-1" />
                  Open Now
                </>
              ) : (
                <>
                  <X className="w-3 h-3 mr-1" />
                  Closed
                </>
              )}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* AOG Banner */}
        {aogSupport && (
          <div className="flex items-center gap-3 p-3 rounded-lg bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/10 border border-amber-200/60 dark:border-amber-700/40">
            <div className="p-2 rounded-full bg-amber-500/20">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="font-bold text-amber-700 dark:text-amber-400">AOG Support Available 24/7</p>
              {aogResponseTime && (
                <p className="text-sm text-amber-600/80 dark:text-amber-400/80">Response time: {aogResponseTime}</p>
              )}
            </div>
          </div>
        )}

        {/* Weekly Schedule */}
        {hasHoursData && (
          <div className="space-y-2">
            {DAYS_ORDER.map((day) => {
              const dayRaw = operatingHours[day];
              const dayData = parseDayHours(dayRaw);
              const isOpen = dayData.open;
              const isToday = day === currentDay;

              return (
                <div
                  key={day}
                  className={cn(
                    "flex items-center justify-between p-2 rounded-lg transition-colors",
                    isToday && "bg-primary/5 border border-primary/20",
                    !isToday && "hover:bg-muted/50"
                  )}
                >
                  <div className="flex items-center gap-2">
                    {isToday && (
                      <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                    )}
                    <span className={cn(
                      "font-medium w-12",
                      isToday && "text-primary"
                    )}>
                      {DAY_LABELS[day]}
                    </span>
                  </div>
                  
                  {isOpen && dayData.start && dayData.end ? (
                    <div className="flex items-center gap-2 text-sm">
                      <Sun className="w-3.5 h-3.5 text-amber-500" />
                      <span className="font-mono">{formatTime(dayData.start)}</span>
                      <span className="text-muted-foreground">-</span>
                      <Moon className="w-3.5 h-3.5 text-indigo-500" />
                      <span className="font-mono">{formatTime(dayData.end)}</span>
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">Closed</span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Shift Coverage & Timezone */}
        <div className="flex flex-wrap gap-3 pt-2 border-t">
          {shiftCoverage && (
            <Badge variant="secondary" className="gap-1">
              <Clock className="w-3 h-3" />
              {shiftCoverage.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </Badge>
          )}
          {timezone && (
            <Badge variant="outline" className="gap-1">
              <Clock className="w-3 h-3" />
              {timezone}
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ServiceOperatingHoursDisplay;
